import React, { useState, useEffect, useRef } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { supabase } from '../../lib/supabase';
import { 
  X, Calendar, Clock, MapPin, Lightbulb, CalendarPlus, Lock, FileText, 
  Users, ChevronRight, Sparkles, Upload, Image as ImageIcon, Link as LinkIcon,
  Hammer, Palette, Coffee, Store, Gamepad2, Trophy, Film, Mountain, Home, 
  UtensilsCrossed, GraduationCap, Plane, TreePine
} from 'lucide-react';
import { format, addDays } from 'date-fns';

// Keywords for smart category detection
const KEYWORDS = {
  'outing-idea': [
    'let\'s go', 'visit', 'zoo', 'museum', 'park', 'restaurant', 'movie', 'beach',
    'bowling', 'mini golf', 'arcade', 'ice cream', 'pizza', 'shopping', 'mall',
    'playground', 'pool', 'lake', 'hiking', 'nature', 'festival', 'fair'
  ],
  'scheduled-event': [
    'dentist', 'doctor', 'appointment', 'practice', 'game', 'meeting', 'class',
    'lesson', 'therapy', 'checkup', 'surgery', 'school', 'conference', 'interview',
    'rehearsal', 'performance', 'recital', 'tournament', 'competition'
  ],
  'personal-event': [
    'my', 'personal', 'private', 'study', 'homework', 'project', 'assignment',
    'work', 'job', 'interview', 'alone time', 'meditation', 'reading', 'writing'
  ],
  'other-item': [
    'birthday', 'holiday', 'prime day', 'black friday', 'vacation', 'break',
    'off work', 'anniversary', 'graduation', 'wedding', 'party', 'celebration',
    'deadline', 'reminder', 'payment', 'bill', 'tax', 'insurance'
  ]
};

// Category configurations
const CATEGORIES = [
  {
    key: 'outing-idea',
    icon: <Lightbulb className="w-6 h-6" />,
    title: 'Family Outing Idea',
    description: 'Something to vote on and maybe do',
    color: 'bg-emerald-500',
    buttonText: 'Add to Ideas'
  },
  {
    key: 'scheduled-event',
    icon: <Calendar className="w-6 h-6" />,
    title: 'Scheduled Event',
    description: 'Appointments, practices, etc.',
    color: 'bg-azure-500',
    buttonText: 'Add to Calendar'
  },
  {
    key: 'personal-event',
    icon: <Lock className="w-6 h-6" />,
    title: 'Personal Event',
    description: 'Just for me, private to others',
    color: 'bg-violet-500',
    buttonText: 'Add to My Calendar'
  },
  {
    key: 'other-item',
    icon: <FileText className="w-6 h-6" />,
    title: 'Other Item',
    description: 'Birthdays, holidays, reminders',
    color: 'bg-orange-500',
    buttonText: 'Add Item'
  }
];

export default function SmartAddModal({ isOpen, onClose }) {
  const { addFamilyActivity, addActivityToCalendar, family, currentUser } = useFamilyContext();
  
  const [inputValue, setInputValue] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [suggestedCategory, setSuggestedCategory] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  
  // Form fields that appear based on category
  const [detailsData, setDetailsData] = useState({
    // Outing Idea fields - comprehensive
    description: '',
    outingCategory: 'outdoor-adventures',
    location: '',
    when: '',
    startDate: '',
    endDate: '',
    isMultiDay: false,
    links: [''],
    image: null,
    colorTheme: 'gold',
    addToFamilyCalendar: false,
    
    // Scheduled Event fields
    dateTime: format(addDays(new Date(), 1), "yyyy-MM-dd'T'HH:mm"),
    endDateTime: format(addDays(new Date(), 1), "yyyy-MM-dd'T'HH:mm"),
    isScheduledMultiDay: false,
    whoInvolved: [], // Changed to array for multi-select
    
    // Personal Event fields
    personalDateTime: format(addDays(new Date(), 1), "yyyy-MM-dd'T'HH:mm"),
    personalEndDateTime: format(addDays(new Date(), 1), "yyyy-MM-dd'T'HH:mm"),
    isPersonalMultiDay: false,
    privacy: 'only-me',
    
    // Other Item fields
    optionalDate: '',
    optionalEndDate: '',
    isOtherMultiDay: false,
    itemType: 'general-reminder'
  });

  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const descriptionRef = useRef(null);
  const dateTimeRef = useRef(null);
  const personalDateTimeRef = useRef(null);
  const optionalDateRef = useRef(null);
  // Plain language input refs
  const scheduledPlainRef = useRef(null);
  const personalPlainRef = useRef(null);
  const otherPlainRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Outing categories with icons
  const outingCategories = [
    { value: 'woodworking', label: 'Woodworking', icon: <Hammer className="w-4 h-4" /> },
    { value: 'art-craft', label: 'Art & Craft', icon: <Palette className="w-4 h-4" /> },
    { value: 'casual-bites', label: 'Casual Bites', icon: <Coffee className="w-4 h-4" /> },
    { value: 'specialty-shops', label: 'Specialty Shops', icon: <Store className="w-4 h-4" /> },
    { value: 'games-interactive', label: 'Games & Interactive Fun', icon: <Gamepad2 className="w-4 h-4" /> },
    { value: 'sports-shows', label: 'Sports & Live Shows', icon: <Trophy className="w-4 h-4" /> },
    { value: 'movies-music', label: 'Movies & Music', icon: <Film className="w-4 h-4" /> },
    { value: 'outdoor-adventures', label: 'Outdoor Adventures', icon: <Mountain className="w-4 h-4" /> },
    { value: 'at-home-fun', label: 'At-Home Fun', icon: <Home className="w-4 h-4" /> },
    { value: 'dining-out', label: 'Dining Out', icon: <UtensilsCrossed className="w-4 h-4" /> },
    { value: 'educational', label: 'Educational Experiences', icon: <GraduationCap className="w-4 h-4" /> },
    { value: 'travel-trips', label: 'Travel & Day Trips', icon: <Plane className="w-4 h-4" /> },
    { value: 'festivals-events', label: 'Festivals & Events', icon: <TreePine className="w-4 h-4" /> },
    { value: 'other', label: 'Other', icon: <Sparkles className="w-4 h-4" /> }
  ];

  // Color theme options for activities without images
  const colorThemes = [
    { value: 'magenta', label: 'Magenta', color: '#e91e63', gradient: 'gradient-magenta' },
    { value: 'coral', label: 'Coral', color: '#ff6b6b', gradient: 'gradient-coral' },
    { value: 'deep-orange', label: 'Deep Orange', color: '#ff7043', gradient: 'gradient-deep-orange' },
    { value: 'gold', label: 'Gold', color: '#ffd54f', gradient: 'gradient-gold' },
    { value: 'teal', label: 'Teal', color: '#26a69a', gradient: 'gradient-teal' },
    { value: 'azure', label: 'azure', color: '#42a5f5', gradient: 'gradient-azure' },
    { value: 'indigo', label: 'Indigo', color: '#7e57c2', gradient: 'gradient-indigo' },
    { value: 'violet', label: 'Violet', color: '#ab47bc', gradient: 'gradient-violet' }
  ];

  // Privacy options for personal events
  const privacyOptions = [
    { value: 'only-me', label: 'Only I can see', icon: '🔒', description: 'Completely private' },
    { value: 'show-busy', label: 'Show as busy', icon: '⏰', description: 'Others see you\'re busy' },
    { value: 'parents-only', label: 'Parents only', icon: '👨‍👩‍👧‍👦', description: 'Visible to parents' }
  ];

  // Item types for other items
  const itemTypes = [
    { value: 'general-reminder', label: 'General Reminder', icon: '📝' },
    { value: 'birthday', label: 'Birthday', icon: '🎂' },
    { value: 'holiday', label: 'Holiday', icon: '🎉' },
    { value: 'special-day', label: 'Special Day', icon: '⭐' },
    { value: 'deadline', label: 'Deadline', icon: '⏰' },
    { value: 'bill-payment', label: 'Bill Payment', icon: '💳' },
    { value: 'subscription', label: 'Subscription', icon: '🔄' },
    { value: 'maintenance', label: 'Home Maintenance', icon: '🏠' }
  ];

  // Location quick options
  const quickLocations = ['Raleigh', 'Cary', 'Apex', 'Holly Springs', 'Fuquay Varina'];

  // Smart date/time detection function
  const detectDateTimeFromText = (text) => {
    if (!text) return null;

    const now = new Date();
    const currentYear = now.getFullYear();

    // Time patterns
    const timePatterns = [
      /(\d{1,2})(?::(\d{2}))?\s*(am|pm)/i,
      /(\d{1,2})(?::(\d{2}))?(?:\s*(am|pm))?/,
      /at\s+(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/i
    ];

    // Date patterns (order matters - more specific patterns first)
    const datePatterns = [
      // Date ranges like "july 8-11", "june 15-20", "dec 24-26"
      /(january|february|march|april|may|june|july|august|september|october|november|december)\s+(\d{1,2})\s*-\s*(\d{1,2})(?:,?\s*(\d{4}))?/i,
      /(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)\s+(\d{1,2})\s*-\s*(\d{1,2})(?:,?\s*(\d{4}))?/i,
      // Numeric date ranges like "6/17-20", "12/24-26"
      /(\d{1,2})\/(\d{1,2})\s*-\s*(\d{1,2})(?:\/(\d{2,4}))?/,
      /(\d{1,2})-(\d{1,2})\s*-\s*(\d{1,2})(?:-(\d{2,4}))?/,
      // Single dates
      /(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?/, // 6/17, 6/17/24, 6/17/2024
      /(\d{1,2})-(\d{1,2})(?:-(\d{2,4}))?/, // 6-17, 6-17-24
      /(january|february|march|april|may|june|july|august|september|october|november|december)\s+(\d{1,2})(?:,?\s*(\d{4}))?/i,
      /(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)\s+(\d{1,2})(?:,?\s*(\d{4}))?/i,
      /(tomorrow|today|next\s+week|next\s+monday|next\s+tuesday|next\s+wednesday|next\s+thursday|next\s+friday|next\s+saturday|next\s+sunday)/i
    ];

    let detectedDate = null;
    let detectedTime = null;

    // Detect time
    for (const pattern of timePatterns) {
      const timeMatch = text.match(pattern);
      if (timeMatch) {
        let hour = parseInt(timeMatch[1]);
        const minute = timeMatch[2] ? parseInt(timeMatch[2]) : 0;
        const ampm = timeMatch[3]?.toLowerCase();

        if (ampm === 'pm' && hour !== 12) hour += 12;
        if (ampm === 'am' && hour === 12) hour = 0;
        if (!ampm && hour >= 1 && hour <= 12 && hour < 8) hour += 12; // Assume PM for afternoon hours

        detectedTime = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        break;
      }
    }

    // Detect date
    let detectedEndDate = null;
    for (const pattern of datePatterns) {
      const dateMatch = text.match(pattern);
      if (dateMatch) {
        if (pattern.source.includes('tomorrow|today|next')) {
          // Handle relative dates
          const relativeDate = new Date();
          const relative = dateMatch[1].toLowerCase();
          
          if (relative === 'tomorrow') {
            relativeDate.setDate(relativeDate.getDate() + 1);
          } else if (relative === 'today') {
            // Keep current date
          } else if (relative.startsWith('next week')) {
            relativeDate.setDate(relativeDate.getDate() + 7);
          } else if (relative.startsWith('next ')) {
            const day = relative.split(' ')[1];
            const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
            const targetDay = days.indexOf(day);
            if (targetDay !== -1) {
              const currentDay = relativeDate.getDay();
              let daysToAdd = targetDay - currentDay;
              if (daysToAdd <= 0) daysToAdd += 7;
              relativeDate.setDate(relativeDate.getDate() + daysToAdd);
            }
          }
          
          detectedDate = format(relativeDate, 'yyyy-MM-dd');
        } else if (pattern.source.includes('\\s*-\\s*')) {
          // Handle date ranges
          const months = {
            january: 0, february: 1, march: 2, april: 3, may: 4, june: 5,
            july: 6, august: 7, september: 8, october: 9, november: 10, december: 11,
            jan: 0, feb: 1, mar: 2, apr: 3, jun: 5,
            jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11
          };
          
          if (isNaN(parseInt(dateMatch[1]))) {
            // Month name range like "july 8-11"
            const month = months[dateMatch[1].toLowerCase()];
            const startDay = parseInt(dateMatch[2]);
            const endDay = parseInt(dateMatch[3]);
            const year = dateMatch[4] ? parseInt(dateMatch[4]) : currentYear;
            
            if (month !== undefined && startDay >= 1 && startDay <= 31 && endDay >= 1 && endDay <= 31) {
              const startDate = new Date(year, month, startDay);
              const endDate = new Date(year, month, endDay);
              detectedDate = format(startDate, 'yyyy-MM-dd');
              detectedEndDate = format(endDate, 'yyyy-MM-dd');
            }
          } else {
            // Numeric range like "6/17-20" or "6-17-20"
            const month = parseInt(dateMatch[1]) - 1;
            const startDay = parseInt(dateMatch[2]);
            const endDay = parseInt(dateMatch[3]);
            let year = currentYear;
            
            if (dateMatch[4]) {
              year = parseInt(dateMatch[4]);
              if (year < 100) year += 2000;
            }
            
            if (month >= 0 && month <= 11 && startDay >= 1 && startDay <= 31 && endDay >= 1 && endDay <= 31) {
              const startDate = new Date(year, month, startDay);
              const endDate = new Date(year, month, endDay);
              if (startDate < now && !dateMatch[4]) {
                startDate.setFullYear(currentYear + 1);
                endDate.setFullYear(currentYear + 1);
              }
              detectedDate = format(startDate, 'yyyy-MM-dd');
              detectedEndDate = format(endDate, 'yyyy-MM-dd');
            }
          }
        } else if (pattern.source.includes('january|february')) {
          // Handle month names (single date)
          const months = {
            january: 0, february: 1, march: 2, april: 3, may: 4, june: 5,
            july: 6, august: 7, september: 8, october: 9, november: 10, december: 11,
            jan: 0, feb: 1, mar: 2, apr: 3, jun: 5,
            jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11
          };
          
          const month = months[dateMatch[1].toLowerCase()];
          const day = parseInt(dateMatch[2]);
          const year = dateMatch[3] ? parseInt(dateMatch[3]) : currentYear;
          
          if (month !== undefined && day >= 1 && day <= 31) {
            const date = new Date(year, month, day);
            detectedDate = format(date, 'yyyy-MM-dd');
          }
        } else {
          // Handle numeric dates (6/17, 6-17)
          const month = parseInt(dateMatch[1]) - 1; // JavaScript months are 0-indexed
          const day = parseInt(dateMatch[2]);
          let year = currentYear;
          
          if (dateMatch[3]) {
            year = parseInt(dateMatch[3]);
            if (year < 100) year += 2000; // Convert 2-digit years
          }
          
          if (month >= 0 && month <= 11 && day >= 1 && day <= 31) {
            const date = new Date(year, month, day);
            // If date is in the past, assume next year
            if (date < now && !dateMatch[3]) {
              date.setFullYear(currentYear + 1);
            }
            detectedDate = format(date, 'yyyy-MM-dd');
          }
        }
        break;
      }
    }

    if (detectedDate || detectedTime) {
      return {
        date: detectedDate,
        endDate: detectedEndDate,
        time: detectedTime,
        dateTime: detectedDate && detectedTime 
          ? `${detectedDate}T${detectedTime}` 
          : detectedDate 
            ? `${detectedDate}T09:00`
            : null,
        endDateTime: detectedEndDate && detectedTime
          ? `${detectedEndDate}T${detectedTime}`
          : detectedEndDate
            ? `${detectedEndDate}T17:00`
            : null,
        isRange: !!detectedEndDate
      };
    }

    return null;
  };

  // Auto-focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Handle keyboard navigation
  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter' && inputValue.trim() && !showDetails) {
      e.preventDefault();
      // If there's a suggested category, select it automatically
      if (suggestedCategory) {
        handleCategorySelect(suggestedCategory);
      }
      // Otherwise, if only one category matches, select it
      else if (inputValue.trim()) {
        const lowercaseInput = inputValue.toLowerCase();
        const matchingCategories = Object.keys(KEYWORDS).filter(category => 
          KEYWORDS[category].some(keyword => lowercaseInput.includes(keyword))
        );
        if (matchingCategories.length === 1) {
          handleCategorySelect(matchingCategories[0]);
        }
      }
    }
  };

  // Smart category detection
  useEffect(() => {
    if (!inputValue.trim()) {
      setSuggestedCategory(null);
      return;
    }

    const lowercaseInput = inputValue.toLowerCase();
    
    // Find best matching category
    let bestMatch = null;
    let maxMatches = 0;

    Object.entries(KEYWORDS).forEach(([category, keywords]) => {
      const matches = keywords.filter(keyword => lowercaseInput.includes(keyword)).length;
      if (matches > maxMatches) {
        maxMatches = matches;
        bestMatch = category;
      }
    });

    setSuggestedCategory(bestMatch);
  }, [inputValue]);

  // Handle category selection
  const handleCategorySelect = (categoryKey) => {
    setSelectedCategory(categoryKey);
    setShowDetails(true);
    
    // Apply smart date/time detection for relevant categories
    const detectedDateTime = detectDateTimeFromText(inputValue);
    if (detectedDateTime) {
      switch (categoryKey) {
        case 'scheduled-event':
          if (detectedDateTime.dateTime) {
            updateDetailsData('dateTime', detectedDateTime.dateTime);
            if (detectedDateTime.isRange && detectedDateTime.endDateTime) {
              updateDetailsData('endDateTime', detectedDateTime.endDateTime);
              updateDetailsData('isScheduledMultiDay', true);
            }
          }
          break;
        case 'personal-event':
          if (detectedDateTime.dateTime) {
            updateDetailsData('personalDateTime', detectedDateTime.dateTime);
            if (detectedDateTime.isRange && detectedDateTime.endDateTime) {
              updateDetailsData('personalEndDateTime', detectedDateTime.endDateTime);
              updateDetailsData('isPersonalMultiDay', true);
            }
          }
          break;
        case 'other-item':
          if (detectedDateTime.date) {
            updateDetailsData('optionalDate', detectedDateTime.date);
            if (detectedDateTime.isRange && detectedDateTime.endDate) {
              updateDetailsData('optionalEndDate', detectedDateTime.endDate);
              updateDetailsData('isOtherMultiDay', true);
            }
          }
          break;
        case 'outing-idea':
          if (detectedDateTime.date) {
            updateDetailsData('startDate', detectedDateTime.date);
            updateDetailsData('when', 'specific');
            if (detectedDateTime.isRange && detectedDateTime.endDate) {
              updateDetailsData('endDate', detectedDateTime.endDate);
              updateDetailsData('isMultiDay', true);
            }
          }
          break;
      }
    }
    
    // Focus appropriate field based on category after a brief delay for rendering
    setTimeout(() => {
      switch (categoryKey) {
        case 'outing-idea':
          descriptionRef.current?.focus();
          break;
        case 'scheduled-event':
          scheduledPlainRef.current?.focus();
          break;
        case 'personal-event':
          personalPlainRef.current?.focus();
          break;
        case 'other-item':
          otherPlainRef.current?.focus();
          break;
      }
    }, 100);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!inputValue.trim() || !selectedCategory) {
      return;
    }

    setIsSubmitting(true);

    try {
      const baseData = {
        title: inputValue.trim(),
        description: inputValue.trim(),
        category: 'other',
        created_by: currentUser?.id
      };

      switch (selectedCategory) {
        case 'outing-idea': {
          const cleanLinks = detailsData.links.filter(link => link.trim() !== '');
          await addFamilyActivity({
            ...baseData,
            title: inputValue.trim(),
            description: detailsData.description || inputValue.trim(),
            category: detailsData.outingCategory,
            location: detailsData.location || null,
            start_date: detailsData.startDate || null,
            end_date: detailsData.isMultiDay ? detailsData.endDate : null,
            image: detailsData.image || null,
            color_theme: detailsData.image ? null : detailsData.colorTheme,
            links: cleanLinks.length > 0 ? cleanLinks : null,
            notes: detailsData.when ? `When: ${detailsData.when}` : null,
            confirmed_for_calendar: detailsData.addToFamilyCalendar
          });
          break;
        }

        case 'scheduled-event': {
          const eventDate = new Date(detailsData.dateTime);
          const eventData = {
            ...baseData,
            date: format(eventDate, 'yyyy-MM-dd'),
            time: detailsData.dateTime.includes(':') && !detailsData.dateTime.endsWith('00:00') 
              ? format(eventDate, 'HH:mm') 
              : null,
            participants: detailsData.whoInvolved,
            confirmed_for_calendar: true
          };
          
          // Add end date for multiday events
          if (detailsData.isScheduledMultiDay && detailsData.endDateTime) {
            const endDate = new Date(detailsData.endDateTime);
            eventData.end_date = format(endDate, 'yyyy-MM-dd');
            eventData.end_time = detailsData.endDateTime.includes(':') && !detailsData.endDateTime.endsWith('00:00')
              ? format(endDate, 'HH:mm')
              : null;
          }
          
          await addActivityToCalendar(eventData);
          break;
        }

        case 'personal-event': {
          const personalDate = new Date(detailsData.personalDateTime);
          const personalEventData = {
            ...baseData,
            date: format(personalDate, 'yyyy-MM-dd'),
            time: detailsData.personalDateTime.includes(':') && !detailsData.personalDateTime.endsWith('00:00')
              ? format(personalDate, 'HH:mm')
              : null,
            privacy_level: detailsData.privacy,
            personal_event: true,
            confirmed_for_calendar: true
          };
          
          // Add end date for multiday events
          if (detailsData.isPersonalMultiDay && detailsData.personalEndDateTime) {
            const personalEndDate = new Date(detailsData.personalEndDateTime);
            personalEventData.end_date = format(personalEndDate, 'yyyy-MM-dd');
            personalEventData.end_time = detailsData.personalEndDateTime.includes(':') && !detailsData.personalEndDateTime.endsWith('00:00')
              ? format(personalEndDate, 'HH:mm')
              : null;
          }
          
          await addActivityToCalendar(personalEventData);
          break;
        }

        case 'other-item': {
          const otherItemData = {
            ...baseData,
            date: detailsData.optionalDate || null,
            category: detailsData.itemType,
            type: 'reminder',
            confirmed_for_calendar: true
          };
          
          // Add end date for multiday items
          if (detailsData.isOtherMultiDay && detailsData.optionalEndDate) {
            otherItemData.end_date = detailsData.optionalEndDate;
          }
          
          await addActivityToCalendar(otherItemData);
          break;
        }
      }

      handleClose();
    } catch (error) {
      console.error('Error adding item:', error);
      alert('Failed to add item. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle going back to category selection
  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setShowDetails(false);
    // Focus back on main input
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  // Handle modal close
  const handleClose = () => {
    setInputValue('');
    setSelectedCategory(null);
    setSuggestedCategory(null);
    setShowDetails(false);
    setDetailsData({
      description: '',
      outingCategory: 'outdoor-adventures',
      location: '',
      when: '',
      startDate: '',
      endDate: '',
      isMultiDay: false,
      links: [''],
      image: null,
      colorTheme: 'teal',
      addToFamilyCalendar: false,
      dateTime: format(addDays(new Date(), 1), "yyyy-MM-dd'T'HH:mm"),
      endDateTime: format(addDays(new Date(), 1), "yyyy-MM-dd'T'HH:mm"),
      isScheduledMultiDay: false,
      whoInvolved: [],
      personalDateTime: format(addDays(new Date(), 1), "yyyy-MM-dd'T'HH:mm"),
      personalEndDateTime: format(addDays(new Date(), 1), "yyyy-MM-dd'T'HH:mm"),
      isPersonalMultiDay: false,
      privacy: 'only-me',
      optionalDate: '',
      optionalEndDate: '',
      isOtherMultiDay: false,
      itemType: 'general-reminder'
    });
    onClose();
  };

  // Handle detail data changes
  const updateDetailsData = (field, value) => {
    setDetailsData(prev => ({ ...prev, [field]: value }));
  };

  // Handle participant toggle for scheduled events
  const toggleParticipant = (participantId) => {
    setDetailsData(prev => ({
      ...prev,
      whoInvolved: prev.whoInvolved.includes(participantId)
        ? prev.whoInvolved.filter(id => id !== participantId)
        : [...prev.whoInvolved, participantId]
    }));
  };

  // Get avatar component for family member
  const renderAvatar = (member) => {
    if (member.avatar && member.avatar.match(/^https?:\/\//)) {
      return (
        <img 
          src={member.avatar} 
          alt={member.name} 
          className="w-8 h-8 rounded-full object-cover"
        />
      );
    }
    if (member.avatar && member.avatar.length <= 3) {
      return (
        <div className="w-8 h-8 rounded-full bg-stone-200 flex items-center justify-center text-lg">
          {member.avatar}
        </div>
      );
    }
    return (
      <div className="w-8 h-8 rounded-full bg-stone-200 flex items-center justify-center text-sm font-bold text-stone-700">
        {member.name?.[0] || '?'}
      </div>
    );
  };

  // Handle image upload
  const handleImageUpload = async (file) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be smaller than 5MB');
      return;
    }

    setUploadingImage(true);

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `family-activity-${Date.now()}.${fileExt}`;
      const filePath = `family/${fileName}`;

      const { error } = await supabase.storage
        .from('family-images')
        .upload(filePath, file);

      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from('family-images')
        .getPublicUrl(filePath);

      updateDetailsData('image', publicUrl);
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  // Handle file input change
  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  // Handle link field changes
  const updateLinkField = (index, value) => {
    const newLinks = [...detailsData.links];
    newLinks[index] = value;
    updateDetailsData('links', newLinks);
  };

  const addLinkField = () => {
    updateDetailsData('links', [...detailsData.links, '']);
  };

  const removeLinkField = (index) => {
    if (detailsData.links.length > 1) {
      const newLinks = detailsData.links.filter((_, i) => i !== index);
      updateDetailsData('links', newLinks);
    }
  };

  if (!isOpen) return null;

  const selectedCategoryConfig = CATEGORIES.find(c => c.key === selectedCategory);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-stone-200">
          <h2 className="text-xl font-bold text-stone-900">Add to Family</h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5 text-stone-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {/* Primary Input */}
          <div className="mb-6">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleInputKeyDown}
              placeholder="Let's go to the zoo, Dentist appointment, Amazon Prime Day..."
              className="w-full px-4 py-4 border-2 border-stone-200 rounded-xl text-lg focus:border-gold-500 focus:outline-none transition-colors bg-stone-50 focus:bg-white"
              disabled={isSubmitting}
            />
            {inputValue.trim() && !showDetails && suggestedCategory && (
              <p className="text-sm text-stone-500 mt-2">
                💡 Press Enter to select suggested category
              </p>
            )}
          </div>

          {/* Category Selection - Always Show */}
          {!showDetails && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-stone-700 mb-4">Choose what type of item to add:</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {CATEGORIES.map((category) => {
                  const isSuggested = suggestedCategory === category.key;
                  return (
                    <button
                      key={category.key}
                      type="button"
                      onClick={() => handleCategorySelect(category.key)}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        isSuggested
                          ? 'border-gold-500 bg-gold-50 shadow-lg transform scale-105'
                          : 'border-stone-200 bg-white hover:border-gold-300 hover:bg-gold-50'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg text-white ${category.color}`}>
                          {category.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-stone-900 flex items-center gap-2">
                            {category.title}
                            {isSuggested && (
                              <div className="flex items-center gap-1 text-xs bg-gold-600 text-white px-2 py-1 rounded-full">
                                <Sparkles className="w-3 h-3" />
                                Suggested
                              </div>
                            )}
                          </div>
                          <div className="text-sm text-stone-600 mt-1">{category.description}</div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-stone-400 flex-shrink-0" />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Progressive Details Form */}
          {showDetails && selectedCategoryConfig && (
            <div className="mb-6 p-4 border border-stone-200 rounded-xl bg-stone-50">
              <div className="flex items-center gap-3 mb-4">
                <button
                  type="button"
                  onClick={handleBackToCategories}
                  className="p-2 hover:bg-stone-200 rounded-lg transition-colors"
                  aria-label="Back to category selection"
                >
                  <ChevronRight className="w-4 h-4 text-stone-500 rotate-180" />
                </button>
                <div className={`p-2 rounded-lg text-white ${selectedCategoryConfig.color}`}>
                  {selectedCategoryConfig.icon}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-stone-900">{selectedCategoryConfig.title}</h3>
                  <p className="text-sm text-stone-600">{selectedCategoryConfig.description}</p>
                </div>
              </div>

              {/* Category-specific fields */}
              {selectedCategory === 'outing-idea' && (
                <div className="space-y-6">
                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Description
                    </label>
                    <textarea
                      ref={descriptionRef}
                      value={detailsData.description}
                      onChange={(e) => updateDetailsData('description', e.target.value)}
                      placeholder="Tell us more about this idea..."
                      rows={3}
                      className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:border-gold-500 focus:outline-none resize-none"
                    />
                  </div>

                  {/* Outing Category */}
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-3">
                      Category
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {outingCategories.slice(0, 6).map((category) => (
                        <button
                          key={category.value}
                          type="button"
                          onClick={() => updateDetailsData('outingCategory', category.value)}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-colors ${
                            detailsData.outingCategory === category.value
                              ? 'bg-gold-100 border-gold-500 text-gold-700'
                              : 'bg-white border-stone-200 text-stone-600 hover:border-stone-300'
                          }`}
                        >
                          {category.icon}
                          <span className="truncate">{category.label}</span>
                        </button>
                      ))}
                    </div>
                    <details className="mt-2">
                      <summary className="text-sm text-stone-500 cursor-pointer hover:text-stone-700">
                        Show more categories
                      </summary>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        {outingCategories.slice(6).map((category) => (
                          <button
                            key={category.value}
                            type="button"
                            onClick={() => updateDetailsData('outingCategory', category.value)}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-colors ${
                              detailsData.outingCategory === category.value
                                ? 'bg-gold-100 border-gold-500 text-gold-700'
                                : 'bg-white border-stone-200 text-stone-600 hover:border-stone-300'
                            }`}
                          >
                            {category.icon}
                            <span className="truncate">{category.label}</span>
                          </button>
                        ))}
                      </div>
                    </details>
                  </div>

                  {/* Image Upload */}
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Image (optional)
                    </label>
                    {detailsData.image ? (
                      <div className="relative">
                        <img 
                          src={detailsData.image} 
                          alt="Activity" 
                          className="w-full h-32 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => updateDetailsData('image', null)}
                          className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div 
                        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                          dragOver 
                            ? 'border-gold-500 bg-gold-50' 
                            : 'border-stone-300 hover:border-stone-400'
                        }`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                      >
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleFileInputChange}
                          className="hidden"
                        />
                        <ImageIcon className="w-8 h-8 text-stone-400 mx-auto mb-2" />
                        <p className="text-sm text-stone-600 mb-2">
                          Drag & drop an image, or{' '}
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="text-gold-600 hover:text-gold-700 font-medium"
                            disabled={uploadingImage}
                          >
                            browse
                          </button>
                        </p>
                        <p className="text-xs text-stone-500">PNG, JPG up to 5MB</p>
                        {uploadingImage && (
                          <div className="mt-2">
                            <div className="w-6 h-6 border-2 border-gold-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Color Theme (only if no image) */}
                  {!detailsData.image && (
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">
                        Color Theme
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {colorThemes.map((theme) => (
                          <button
                            key={theme.value}
                            type="button"
                            onClick={() => updateDetailsData('colorTheme', theme.value)}
                            className={`w-8 h-8 rounded-full border-2 transition-all ${
                              detailsData.colorTheme === theme.value
                                ? 'border-stone-900 scale-110'
                                : 'border-stone-300 hover:scale-105'
                            }`}
                            style={{ backgroundColor: theme.color }}
                            title={theme.label}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Location */}
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Where?
                    </label>
                    <div className="space-y-3">
                      {/* Quick select location buttons */}
                      <div className="flex flex-wrap gap-2">
                        {quickLocations.map((location) => (
                          <button
                            key={location}
                            type="button"
                            onClick={() => updateDetailsData('location', location)}
                            className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                              detailsData.location === location
                                ? 'bg-gold-100 border-gold-500 text-gold-700'
                                : 'bg-white border-stone-300 text-stone-600 hover:border-stone-400'
                            }`}
                          >
                            {location}
                          </button>
                        ))}
                      </div>
                      {/* Custom location input */}
                      <input
                        type="text"
                        value={detailsData.location}
                        onChange={(e) => updateDetailsData('location', e.target.value)}
                        placeholder="Add location (optional)..."
                        className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:border-gold-500 focus:outline-none"
                      />
                    </div>
                  </div>

                  {/* When/Date Options */}
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      When would be good?
                    </label>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {['Next weekend', 'Someday', 'Summer', 'Pick dates'].map((option) => (
                        <button
                          key={option}
                          type="button"
                          onClick={() => {
                            if (option === 'Pick dates') {
                              updateDetailsData('when', 'specific');
                            } else {
                              updateDetailsData('when', option);
                              updateDetailsData('startDate', '');
                              updateDetailsData('endDate', '');
                            }
                          }}
                          className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                            (detailsData.when === option || (option === 'Pick dates' && detailsData.when === 'specific'))
                              ? 'bg-gold-100 border-gold-500 text-gold-700'
                              : 'bg-white border-stone-300 text-stone-600 hover:border-stone-400'
                          }`}
                        >
                          {option}
                        </button>
                      ))}
                    </div>

                    {/* Specific Date Picker */}
                    {detailsData.when === 'specific' && (
                      <div className="space-y-3 p-3 bg-stone-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="multiDay"
                            checked={detailsData.isMultiDay}
                            onChange={(e) => updateDetailsData('isMultiDay', e.target.checked)}
                            className="rounded border-stone-300 text-gold-600 focus:ring-gold-500"
                          />
                          <label htmlFor="multiDay" className="text-sm text-stone-700">
                            Multi-day event (like a festival)
                          </label>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-stone-600 mb-1">
                              {detailsData.isMultiDay ? 'Start Date' : 'Date'}
                            </label>
                            <input
                              type="date"
                              value={detailsData.startDate}
                              onChange={(e) => updateDetailsData('startDate', e.target.value)}
                              className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:border-gold-500 focus:outline-none"
                            />
                          </div>
                          {detailsData.isMultiDay && (
                            <div>
                              <label className="block text-xs font-medium text-stone-600 mb-1">
                                End Date
                              </label>
                              <input
                                type="date"
                                value={detailsData.endDate}
                                onChange={(e) => updateDetailsData('endDate', e.target.value)}
                                min={detailsData.startDate}
                                className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:border-gold-500 focus:outline-none"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Links */}
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Related Links (optional)
                    </label>
                    {detailsData.links.map((link, index) => (
                      <div key={index} className="flex gap-2 mb-2">
                        <div className="flex-1 relative">
                          <LinkIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-stone-400" />
                          <input
                            type="url"
                            value={link}
                            onChange={(e) => updateLinkField(index, e.target.value)}
                            placeholder="https://example.com"
                            className="w-full pl-10 pr-3 py-2 border border-stone-300 rounded-lg focus:border-gold-500 focus:outline-none"
                          />
                        </div>
                        {detailsData.links.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeLinkField(index)}
                            className="p-2 text-red-500 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                    {detailsData.links.length < 3 && (
                      <button
                        type="button"
                        onClick={addLinkField}
                        className="text-sm text-gold-600 hover:text-gold-700 font-medium"
                      >
                        + Add another link
                      </button>
                    )}
                  </div>

                  {/* Add to Family Calendar */}
                  <div className="pt-4 border-t border-stone-200">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="addToFamilyCalendar"
                        checked={detailsData.addToFamilyCalendar}
                        onChange={(e) => updateDetailsData('addToFamilyCalendar', e.target.checked)}
                        className="mt-1 w-4 h-4 text-gold-600 border-stone-300 rounded focus:ring-gold-500"
                      />
                      <div className="flex-1">
                        <label htmlFor="addToFamilyCalendar" className="block text-sm font-medium text-stone-700 cursor-pointer">
                          Add to Family Calendar
                        </label>
                        <p className="text-xs text-stone-500 mt-1 leading-relaxed">
                          Check this to confirm the activity for your family calendar and open it for RSVPs. Leave unchecked if dates are tentative (like a festival that has specific dates but you haven't decided to attend yet).
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {selectedCategory === 'scheduled-event' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Date & Time
                    </label>
                    <div className="space-y-3">
                      {/* Plain language input for date/time */}
                      <input
                        ref={scheduledPlainRef}
                        type="text"
                        placeholder="Tomorrow at 2pm, June 15th at 9am, Next Friday at 10:30..."
                        className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:border-gold-500 focus:outline-none text-sm"
                        onBlur={(e) => {
                          const detected = detectDateTimeFromText(e.target.value);
                          if (detected?.dateTime) {
                            updateDetailsData('dateTime', detected.dateTime);
                            if (detected.isRange && detected.endDateTime) {
                              updateDetailsData('endDateTime', detected.endDateTime);
                              updateDetailsData('isScheduledMultiDay', true);
                            }
                            e.target.value = ''; // Clear the input after successful detection
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const detected = detectDateTimeFromText(e.target.value);
                            if (detected?.dateTime) {
                              updateDetailsData('dateTime', detected.dateTime);
                              if (detected.isRange && detected.endDateTime) {
                                updateDetailsData('endDateTime', detected.endDateTime);
                                updateDetailsData('isScheduledMultiDay', true);
                              }
                              e.target.value = ''; // Clear the input after successful detection
                            }
                          }
                        }}
                      />
                      
                      {/* Multi-day checkbox */}
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="scheduledMultiDay"
                          checked={detailsData.isScheduledMultiDay}
                          onChange={(e) => updateDetailsData('isScheduledMultiDay', e.target.checked)}
                          className="rounded border-stone-300 text-azure-600 focus:ring-azure-500"
                        />
                        <label htmlFor="scheduledMultiDay" className="text-sm text-stone-700">
                          Multi-day event
                        </label>
                      </div>
                      
                      {/* Date/time inputs */}
                      <div className="space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-stone-600 mb-1">
                              {detailsData.isScheduledMultiDay ? 'Start Date' : 'Date'}
                            </label>
                            <input
                              ref={dateTimeRef}
                              type="date"
                              value={detailsData.dateTime ? detailsData.dateTime.split('T')[0] : ''}
                              onChange={(e) => {
                                const time = detailsData.dateTime ? detailsData.dateTime.split('T')[1] : '09:00';
                                updateDetailsData('dateTime', e.target.value + 'T' + time);
                              }}
                              className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:border-gold-500 focus:outline-none"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-stone-600 mb-1">
                              {detailsData.isScheduledMultiDay ? 'Start Time (optional)' : 'Time (optional)'}
                            </label>
                            <input
                              type="time"
                              value={detailsData.dateTime ? detailsData.dateTime.split('T')[1] : ''}
                              onChange={(e) => {
                                const date = detailsData.dateTime ? detailsData.dateTime.split('T')[0] : '';
                                updateDetailsData('dateTime', date + 'T' + (e.target.value || '09:00'));
                              }}
                              className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:border-gold-500 focus:outline-none"
                              placeholder="HH:MM"
                            />
                          </div>
                        </div>
                        {detailsData.isScheduledMultiDay && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-stone-600 mb-1">
                                End Date
                              </label>
                              <input
                                type="date"
                                value={detailsData.endDateTime ? detailsData.endDateTime.split('T')[0] : ''}
                                onChange={(e) => {
                                  const time = detailsData.endDateTime ? detailsData.endDateTime.split('T')[1] : '17:00';
                                  updateDetailsData('endDateTime', e.target.value + 'T' + time);
                                }}
                                min={detailsData.dateTime ? detailsData.dateTime.split('T')[0] : ''}
                                className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:border-gold-500 focus:outline-none"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-stone-600 mb-1">
                                End Time (optional)
                              </label>
                              <input
                                type="time"
                                value={detailsData.endDateTime ? detailsData.endDateTime.split('T')[1] : ''}
                                onChange={(e) => {
                                  const date = detailsData.endDateTime ? detailsData.endDateTime.split('T')[0] : '';
                                  updateDetailsData('endDateTime', date + 'T' + (e.target.value || '17:00'));
                                }}
                                className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:border-gold-500 focus:outline-none"
                                placeholder="HH:MM"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Show what was detected */}
                      {(() => {
                        const detected = detectDateTimeFromText(inputValue);
                        if (detected?.dateTime && detailsData.dateTime === detected.dateTime) {
                          return (
                            <p className="text-xs text-gold-600 mt-1 flex items-center gap-1">
                              <span>✨</span>
                              Auto-detected from: "{inputValue}"
                            </p>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-3">
                      Who's involved?
                    </label>
                    <div className="space-y-2">
                      {/* Parents */}
                      {family?.parents?.map((parent) => (
                        <button
                          key={parent.id}
                          type="button"
                          onClick={() => toggleParticipant(parent.id)}
                          className={`flex items-center gap-3 w-full p-3 rounded-lg border-2 transition-all ${
                            detailsData.whoInvolved.includes(parent.id)
                              ? 'border-gold-500 bg-gold-50'
                              : 'border-stone-200 bg-white hover:border-stone-300'
                          }`}
                        >
                          {renderAvatar(parent)}
                          <div className="flex-1 text-left">
                            <div className="font-medium text-stone-900">{parent.name}</div>
                            <div className="text-sm text-stone-500">Parent</div>
                          </div>
                          {detailsData.whoInvolved.includes(parent.id) && (
                            <div className="text-gold-600">
                              <Users className="w-5 h-5" />
                            </div>
                          )}
                        </button>
                      ))}
                      
                      {/* Kids */}
                      {family?.kids?.map((kid) => (
                        <button
                          key={kid.id}
                          type="button"
                          onClick={() => toggleParticipant(kid.id)}
                          className={`flex items-center gap-3 w-full p-3 rounded-lg border-2 transition-all ${
                            detailsData.whoInvolved.includes(kid.id)
                              ? 'border-gold-500 bg-gold-50'
                              : 'border-stone-200 bg-white hover:border-stone-300'
                          }`}
                        >
                          {renderAvatar(kid)}
                          <div className="flex-1 text-left">
                            <div className="font-medium text-stone-900">{kid.name}</div>
                            <div className="text-sm text-stone-500">
                              Age {kid.age || 'Unknown'}
                            </div>
                          </div>
                          {detailsData.whoInvolved.includes(kid.id) && (
                            <div className="text-gold-600">
                              <Users className="w-5 h-5" />
                            </div>
                          )}
                        </button>
                      ))}
                      
                      {/* Quick select buttons */}
                      <div className="flex flex-wrap gap-2 pt-2">
                        <button
                          type="button"
                          onClick={() => {
                            const allIds = [
                              ...(family?.parents?.map(p => p.id) || []),
                              ...(family?.kids?.map(k => k.id) || [])
                            ];
                            updateDetailsData('whoInvolved', allIds);
                          }}
                          className="px-3 py-1 text-sm bg-stone-100 hover:bg-stone-200 rounded-full transition-colors"
                        >
                          Select All
                        </button>
                        <button
                          type="button"
                          onClick={() => updateDetailsData('whoInvolved', [])}
                          className="px-3 py-1 text-sm bg-stone-100 hover:bg-stone-200 rounded-full transition-colors"
                        >
                          Clear All
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const parentIds = family?.parents?.map(p => p.id) || [];
                            updateDetailsData('whoInvolved', parentIds);
                          }}
                          className="px-3 py-1 text-sm bg-stone-100 hover:bg-stone-200 rounded-full transition-colors"
                        >
                          Parents Only
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {selectedCategory === 'personal-event' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Date & Time
                    </label>
                    <div className="space-y-3">
                      {/* Plain language input for date/time */}
                      <input
                        ref={personalPlainRef}
                        type="text"
                        placeholder="Tomorrow at 2pm, June 15th at 9am, Next Friday at 10:30..."
                        className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:border-gold-500 focus:outline-none text-sm"
                        onBlur={(e) => {
                          const detected = detectDateTimeFromText(e.target.value);
                          if (detected?.dateTime) {
                            updateDetailsData('personalDateTime', detected.dateTime);
                            if (detected.isRange && detected.endDateTime) {
                              updateDetailsData('personalEndDateTime', detected.endDateTime);
                              updateDetailsData('isPersonalMultiDay', true);
                            }
                            e.target.value = ''; // Clear the input after successful detection
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const detected = detectDateTimeFromText(e.target.value);
                            if (detected?.dateTime) {
                              updateDetailsData('personalDateTime', detected.dateTime);
                              if (detected.isRange && detected.endDateTime) {
                                updateDetailsData('personalEndDateTime', detected.endDateTime);
                                updateDetailsData('isPersonalMultiDay', true);
                              }
                              e.target.value = ''; // Clear the input after successful detection
                            }
                          }
                        }}
                      />
                      
                      {/* Multi-day checkbox */}
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="personalMultiDay"
                          checked={detailsData.isPersonalMultiDay}
                          onChange={(e) => updateDetailsData('isPersonalMultiDay', e.target.checked)}
                          className="rounded border-stone-300 text-violet-600 focus:ring-violet-500"
                        />
                        <label htmlFor="personalMultiDay" className="text-sm text-stone-700">
                          Multi-day event
                        </label>
                      </div>
                      
                      {/* Date/time inputs */}
                      <div className="space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-stone-600 mb-1">
                              {detailsData.isPersonalMultiDay ? 'Start Date' : 'Date'}
                            </label>
                            <input
                              ref={personalDateTimeRef}
                              type="date"
                              value={detailsData.personalDateTime ? detailsData.personalDateTime.split('T')[0] : ''}
                              onChange={(e) => {
                                const time = detailsData.personalDateTime ? detailsData.personalDateTime.split('T')[1] : '09:00';
                                updateDetailsData('personalDateTime', e.target.value + 'T' + time);
                              }}
                              className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:border-gold-500 focus:outline-none"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-stone-600 mb-1">
                              {detailsData.isPersonalMultiDay ? 'Start Time (optional)' : 'Time (optional)'}
                            </label>
                            <input
                              type="time"
                              value={detailsData.personalDateTime ? detailsData.personalDateTime.split('T')[1] : ''}
                              onChange={(e) => {
                                const date = detailsData.personalDateTime ? detailsData.personalDateTime.split('T')[0] : '';
                                updateDetailsData('personalDateTime', date + 'T' + (e.target.value || '09:00'));
                              }}
                              className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:border-gold-500 focus:outline-none"
                              placeholder="HH:MM"
                            />
                          </div>
                        </div>
                        {detailsData.isPersonalMultiDay && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-stone-600 mb-1">
                                End Date
                              </label>
                              <input
                                type="date"
                                value={detailsData.personalEndDateTime ? detailsData.personalEndDateTime.split('T')[0] : ''}
                                onChange={(e) => {
                                  const time = detailsData.personalEndDateTime ? detailsData.personalEndDateTime.split('T')[1] : '17:00';
                                  updateDetailsData('personalEndDateTime', e.target.value + 'T' + time);
                                }}
                                min={detailsData.personalDateTime ? detailsData.personalDateTime.split('T')[0] : ''}
                                className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:border-gold-500 focus:outline-none"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-stone-600 mb-1">
                                End Time (optional)
                              </label>
                              <input
                                type="time"
                                value={detailsData.personalEndDateTime ? detailsData.personalEndDateTime.split('T')[1] : ''}
                                onChange={(e) => {
                                  const date = detailsData.personalEndDateTime ? detailsData.personalEndDateTime.split('T')[0] : '';
                                  updateDetailsData('personalEndDateTime', date + 'T' + (e.target.value || '17:00'));
                                }}
                                className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:border-gold-500 focus:outline-none"
                                placeholder="HH:MM"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Show what was detected */}
                      {(() => {
                        const detected = detectDateTimeFromText(inputValue);
                        if (detected?.dateTime && detailsData.personalDateTime === detected.dateTime) {
                          return (
                            <p className="text-xs text-violet-600 mt-1 flex items-center gap-1">
                              <span>✨</span>
                              Auto-detected from: "{inputValue}"
                            </p>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-3">
                      Privacy Level
                    </label>
                    <div className="space-y-2">
                      {privacyOptions.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => updateDetailsData('privacy', option.value)}
                          className={`flex items-center gap-3 w-full p-3 rounded-lg border-2 transition-all text-left ${
                            detailsData.privacy === option.value
                              ? 'border-violet-500 bg-violet-50'
                              : 'border-stone-200 bg-white hover:border-stone-300'
                          }`}
                        >
                          <div className="text-2xl">{option.icon}</div>
                          <div className="flex-1">
                            <div className="font-medium text-stone-900">{option.label}</div>
                            <div className="text-sm text-stone-500">{option.description}</div>
                          </div>
                          {detailsData.privacy === option.value && (
                            <div className="text-violet-600">
                              <Lock className="w-5 h-5" />
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="bg-violet-50 border border-violet-200 rounded-lg p-3">
                    <p className="text-sm text-violet-700 flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      Personal events appear only on your individual calendar view
                    </p>
                  </div>
                </div>
              )}

              {selectedCategory === 'other-item' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Date (if applicable)
                    </label>
                    <div className="space-y-3">
                      {/* Plain language input for date */}
                      <input
                        ref={otherPlainRef}
                        type="text"
                        placeholder="Tomorrow, June 15th, Next Friday, Christmas..."
                        className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:border-gold-500 focus:outline-none text-sm"
                        onBlur={(e) => {
                          const detected = detectDateTimeFromText(e.target.value);
                          if (detected?.date) {
                            updateDetailsData('optionalDate', detected.date);
                            if (detected.isRange && detected.endDate) {
                              updateDetailsData('optionalEndDate', detected.endDate);
                              updateDetailsData('isOtherMultiDay', true);
                            }
                            e.target.value = ''; // Clear the input after successful detection
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const detected = detectDateTimeFromText(e.target.value);
                            if (detected?.date) {
                              updateDetailsData('optionalDate', detected.date);
                              if (detected.isRange && detected.endDate) {
                                updateDetailsData('optionalEndDate', detected.endDate);
                                updateDetailsData('isOtherMultiDay', true);
                              }
                              e.target.value = ''; // Clear the input after successful detection
                            }
                          }
                        }}
                      />
                      
                      {/* Multi-day checkbox */}
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="otherMultiDay"
                          checked={detailsData.isOtherMultiDay}
                          onChange={(e) => updateDetailsData('isOtherMultiDay', e.target.checked)}
                          className="rounded border-stone-300 text-orange-600 focus:ring-orange-500"
                        />
                        <label htmlFor="otherMultiDay" className="text-sm text-stone-700">
                          Multi-day item (like vacation or holiday period)
                        </label>
                      </div>
                      
                      {/* Date inputs */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-stone-600 mb-1">
                            {detailsData.isOtherMultiDay ? 'Start Date' : 'Date'}
                          </label>
                          <input
                            ref={optionalDateRef}
                            type="date"
                            value={detailsData.optionalDate}
                            onChange={(e) => updateDetailsData('optionalDate', e.target.value)}
                            className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:border-gold-500 focus:outline-none"
                          />
                        </div>
                        {detailsData.isOtherMultiDay && (
                          <div>
                            <label className="block text-xs font-medium text-stone-600 mb-1">
                              End Date
                            </label>
                            <input
                              type="date"
                              value={detailsData.optionalEndDate}
                              onChange={(e) => updateDetailsData('optionalEndDate', e.target.value)}
                              min={detailsData.optionalDate}
                              className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:border-gold-500 focus:outline-none"
                            />
                          </div>
                        )}
                      </div>
                      
                      {/* Show what was detected */}
                      {(() => {
                        const detected = detectDateTimeFromText(inputValue);
                        if (detected?.date && detailsData.optionalDate === detected.date) {
                          return (
                            <p className="text-xs text-orange-600 mt-1 flex items-center gap-1">
                              <span>✨</span>
                              Auto-detected from: "{inputValue}"
                            </p>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-3">
                      Item Type
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {itemTypes.map((type) => (
                        <button
                          key={type.value}
                          type="button"
                          onClick={() => updateDetailsData('itemType', type.value)}
                          className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all text-left ${
                            detailsData.itemType === type.value
                              ? 'border-orange-500 bg-orange-50'
                              : 'border-stone-200 bg-white hover:border-stone-300'
                          }`}
                        >
                          <div className="text-lg">{type.icon}</div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-stone-900 text-sm truncate">
                              {type.label}
                            </div>
                          </div>
                          {detailsData.itemType === type.value && (
                            <div className="text-orange-600 flex-shrink-0">
                              <FileText className="w-4 h-4" />
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
            <button
              type="button"
              onClick={handleClose}
              className="px-6 py-3 border border-stone-300 text-stone-700 rounded-lg hover:bg-stone-50 transition-colors font-medium"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!inputValue.trim() || !selectedCategory || isSubmitting}
              className="px-6 py-3 bg-gold-600 text-white rounded-lg hover:bg-gold-700 disabled:bg-stone-300 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Adding...
                </>
              ) : (
                selectedCategoryConfig?.buttonText || 'Add to Family'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}